import React, { ChangeEvent, FC, useState } from 'react';
import {
  ModalStyled,
  ButtonWrapper,
  AddDocument,
  DocumentLoader,
  UploadingProgress,
  UploadingText,
  ImageWrapper,
} from './styles';
import { PrimaryButton, SecondaryButton } from '../index';
import Modal from '@mui/material/Modal';
import { DocumentsUploadProps } from './models';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LoadIcon } from 'application/assets/loader.svg';
import { ReactComponent as AttentionIcon } from 'application/assets/attention.svg';
import {
  ImageIcon,
  PdfIcon,
  DocumentIcon,
  ExelIcon,
} from 'application/assets/document-types';

/**
 * A modal component that allows users to upload documents.
 * @param isOpen - Whether the modal is currently open.
 * @param onClose - A function that will be called when the modal is closed.
 * @param onSave - A function that will be called when the user saves the uploaded document(s).
 * @param isLoading - Whether the document(s) are currently being saved.
 * @param error - Whether there was an error saving the document(s).
 * @param extantions - The file types that can be uploaded (default: ".jpg, .jpeg, .png, .pdf, .doc, .docx, .odt").
 */
const DocumentsUploadModal: FC<DocumentsUploadProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  error = false,
  extantions = '.jpg, .jpeg, .png, .pdf, .doc, .docx, .odt',
}) => {
  const { t } = useTranslation();
  const [documentData, setDocumentData] = useState<FormData | null>();
  const [files, setFiles] = useState<File | null>(null);

  const handleCloseModal = () => {
    setFiles(null);
    setDocumentData(null);
    onClose();
  };

  const handleSaveDocuments = () => {
    onSave(documentData!);
    setFiles(null);
    setDocumentData(null);
  };

  //Gets the appropriate icon for the currently selected file.
  const getCurrentIcon = () => {
    if (
      files?.name.includes('.jpg') ||
      files?.name.includes('.jpeg') ||
      files?.name.includes('.png')
    ) {
      return <ImageIcon />;
    } else if (files?.name.includes('.pdf')) {
      return <PdfIcon />;
    } else if (files?.name.includes('.xlsx')) {
      return <ExelIcon />;
    } else {
      return <DocumentIcon />;
    }
  };
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const filesArr = e.target.files!;
    const file = filesArr[0];
    const formData = new FormData();

    formData.append('documents', file);
    setFiles(file!);
    setDocumentData(formData);
  };

  return (
    <Modal open={isOpen}>
      <ModalStyled>
        <div className={'content-wrapper'}>
          <h6>{t('upload_modal.title')}</h6>

          <AddDocument isLoading={isLoading}>
            <div className={isLoading ? 'disabled' : ''}>
              <label htmlFor="photo" />
              <input
                id="photo"
                type="file"
                accept={extantions}
                onChange={(e) => handleChange(e)}
                disabled={isLoading}
              />
              {files ? (
                <ImageWrapper>
                  {!!error && (
                    <UploadingProgress>
                      <DocumentLoader isError={!!error}>
                        <AttentionIcon />
                      </DocumentLoader>
                    </UploadingProgress>
                  )}
                  {isLoading ? (
                    <UploadingProgress>
                      <DocumentLoader>
                        <LoadIcon />
                      </DocumentLoader>
                    </UploadingProgress>
                  ) : (
                    getCurrentIcon()
                  )}
                  {files?.name}
                </ImageWrapper>
              ) : (
                t('upload_modal.import_file')
              )}
            </div>
          </AddDocument>
          {error ? (
            <UploadingText isError={!!error}>
              <p>{t('editProfileSettings.errorTitle')}</p>
              <p>{t('upload_modal.error')}</p>
            </UploadingText>
          ) : (
            <UploadingText isError={!!error}>
              <p>{extantions} (max. 40 MB)</p>
            </UploadingText>
          )}
        </div>
        <ButtonWrapper>
          <SecondaryButton onClick={handleCloseModal}>
            {t('global.cancel')}
          </SecondaryButton>
          <PrimaryButton onClick={handleSaveDocuments} disabled={isLoading}>
            {t('global.save')}
          </PrimaryButton>
        </ButtonWrapper>
      </ModalStyled>
    </Modal>
  );
};

export default DocumentsUploadModal;
